import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getDb } from '../../firebase/utils';
import { LeaveType } from '../types/leaveType';

/**
 * 휴가 유형 목록 조회
 *
 * V68: 회사 코드별 노출 분리.
 *  - companyCode='KR'      → visibilityScope in ['KR', 'COMMON']
 *  - companyCode='CN'      → visibilityScope in ['CN', 'COMMON']
 *  - companyCode 미지정    → 'KR' fallback (legacy 호출자 호환)
 * 사전 조건: prod/QA leaveTypes 모든 doc 에 visibilityScope 필드 backfill 완료
 *           (없으면 Firestore 'in' 쿼리에서 제외됨).
 */
export function useLeaveTypes(companyCode?: 'KR' | 'CN') {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V68: companyCode 별 visibilityScope 필터 (fallback = 'KR')
        const scopes = companyCode === 'CN' ? ['CN', 'COMMON'] : ['KR', 'COMMON'];

        const q = query(
          collection(getDb(), 'leaveTypes'),
          where('isActive', '==', true),
          where('visibilityScope', 'in', scopes)
        );

        const snapshot = await getDocs(q);
        const types = snapshot.docs.map(doc => ({
          ...doc.data(),
          code: doc.id,
        })) as LeaveType[];

        setLeaveTypes(types);
      } catch (err) {
        console.error('휴가 유형 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeaveTypes();
  }, [companyCode]);

  return { leaveTypes, isLoading, error };
}

/**
 * 특정 휴가 유형 조회
 */
export function useLeaveType(leaveTypeCode?: string) {
  const { leaveTypes, isLoading, error } = useLeaveTypes();
  const [leaveType, setLeaveType] = useState<LeaveType | null>(null);

  useEffect(() => {
    if (!leaveTypeCode || isLoading) {
      setLeaveType(null);
      return;
    }

    const found = leaveTypes.find(lt => lt.code === leaveTypeCode);
    setLeaveType(found || null);
  }, [leaveTypeCode, leaveTypes, isLoading]);

  return { leaveType, isLoading, error };
}
